import { Helpers } from "../Helpers";
import { IDIService } from "../IDIService";

export interface IDataMigrationService {
	IsMigrationDataAvailableAsync(): Promise<boolean>;
	GetLanguageCodeAsync(): Promise<string | null>;
	GetControllersDataAsync(): Promise<ControllerData[] | null>;
}

export class DataMigrationService implements IDataMigrationService, IDIService {
	public static readonly AppSelectedLanguageKeyName: string = "App___SelectedLanguage";
	public static readonly AppUserControllersKeyName: string = "App___UserControllersListDatabase";
	public static readonly ServiceName: string = "DataMigrationService";

	constructor() {
		try {
			const currentUrl = window.location.href;
			const url = new URL(currentUrl);
			const params = new URLSearchParams(url.search);

			const encMig = params.get('mig_c_enc');
			const migration = params.get('mig_controllers');
			const migLang = params.get('mig_lang');

			if (encMig !== null) {
				const decodedData = decodeURIComponent(this.b64DecodeUnicode(decodeURIComponent(encMig)));
				localStorage.setItem(DataMigrationService.AppUserControllersKeyName, decodedData);
			}

			if (migration !== null) {
				localStorage.setItem(DataMigrationService.AppUserControllersKeyName, decodeURIComponent(migration));
			}

			if (migLang !== null) {
				localStorage.setItem(DataMigrationService.AppSelectedLanguageKeyName, migLang);
			}
		}
		catch (ex) {
			console.error(ex);
		}
	}

	private waitForNavigatorObject(propertyName: string): Promise<IExalusDataMigration> {
		return new Promise((resolve, reject) => {
			function check() {
				if ((navigator as any)[propertyName]) {
					resolve((navigator as any)[propertyName]);
				} else {
					requestAnimationFrame(check);
				}
			}

			// Optional: Set a timeout to reject the promise if the object is not found within a reasonable time.
			setTimeout(() => {
				reject(new Error(`Timed out waiting for navigator.${propertyName}`));
			}, 10000); // Adjust the timeout duration as needed

			check();
		});
	}

	private b64DecodeUnicode(str: string): string {
		return decodeURIComponent(Array.prototype.map.call(atob(str), function (c) {
			return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
		}).join(''));
	}

	private decodeUriJsonData<T>(data: string): T {
		return JSON.parse(decodeURIComponent(data));
	}

	private encodeUriJsonData(data: any): string {
		return encodeURIComponent(JSON.stringify(data));
	}

	private IsExalusNativeMigrationAvailable(): boolean {
		return (navigator as any).exalusMigration !== undefined;
	}

	GetServiceName(): string {
		return DataMigrationService.ServiceName;
	}

	public async IsMigrationDataAvailableAsync(): Promise<boolean> {
		try {
			//Ios
			if (Helpers.IsIosNative()) {
				var res = await DataMigrationService.GetIosMigrationStatus();
				return res !== null ? res : false;
			}
			//Andorid
			else {
				if (navigator.userAgent.endsWith("LavvaAndroidNative")) {
					if ((navigator as any).exalusMigration === undefined || (navigator as any).exalusMigration === null) {
						const tmp = await this.waitForNavigatorObject("exalusMigration");
					}
				}
				if (this.IsExalusNativeMigrationAvailable()) {
					let tmp = await ((navigator as any).exalusMigration as IExalusDataMigration)!.IsMigrationDataAvailableAsync();

					if (tmp == null)
						return false;
					else
						return tmp.DataMigrated;
				}
				else if (localStorage.getItem(DataMigrationService.AppSelectedLanguageKeyName) !== null || localStorage.getItem(DataMigrationService.AppUserControllersKeyName) !== null)
					return true;
				else
					return false;
			}
		}
		catch (ex) {
			console.error(ex);
			return false;
		}
	}

	public async GetControllersDataAsync(): Promise<ControllerData[] | null> {
		try {
			//Ios
			if (Helpers.IsIosNative()) {
				let tmp = await DataMigrationService.GetIosControllerList();

				if (tmp === undefined)
					return null;
				else
					return tmp;
			}
			//Andorid
			else {
				if (this.IsExalusNativeMigrationAvailable()) {
					let controllers = await ((navigator as any).exalusMigration as IExalusDataMigration)!.GetControllersDataAsync();

					if (controllers === undefined)
						return null;
					else
						return controllers;
				}

				let controllers = localStorage.getItem(DataMigrationService.AppUserControllersKeyName);

				if (controllers !== null)
					return JSON.parse(controllers);
				else
					return null;
			}
		}
		catch (ex) {
			console.error(ex);
			return null;
		}
	}

	public async GetLanguageCodeAsync(): Promise<string | null> {
		try {
			//Ios
			if (Helpers.IsIosNative()) {
				let tmp = await DataMigrationService.GetIosDefaultLanguage();

				if (tmp === undefined)
					return null;
				else
					return tmp;
			}
			else {
				if (this.IsExalusNativeMigrationAvailable()) {
					let tmp = await ((navigator as any).exalusMigration as IExalusDataMigration)!.GetLanguageCodeAsync();

					if (tmp === undefined)
						return null;
					else
						return tmp;
				}

				let local = localStorage.getItem(DataMigrationService.AppSelectedLanguageKeyName);

				if (local !== undefined && local !== null)
					return local;
				else
					return null;
			}
		}
		catch (ex) {
			console.error(ex);
			return null;
		}
	}

	static GetIosMigrationStatus(timeout = 8_000): Promise<boolean | null> {
		let timer: NodeJS.Timeout;

		return new Promise((resolve, reject) => {
			timer = setTimeout(() => {
				reject(null);
			}, timeout);

			try {
				(window as any).nativeEvent.exalusControllerData.isMigrationAvaliable = (status: boolean) => {
					clearTimeout(timer);
					resolve(status);
				};

				(window as any).webkit.messageHandlers.exalusControllerData.postMessage("IsMigrationAvaliable");
			} catch (err) {
				reject(err);
			}
		});
	}

	static GetIosControllerList(timeout = 8_000): Promise<ControllerData[] | null> {
		let timer: NodeJS.Timeout;

		return new Promise((resolve, reject) => {
			timer = setTimeout(() => {
				reject(null);
			}, timeout);

			try {
				(window as any).nativeEvent.exalusControllerData.handleControllers = (data: ControllerData[]) => {
					clearTimeout(timer);
					resolve(data);
				};

				(window as any).webkit.messageHandlers.exalusControllerData.postMessage("GetControllers");
			} catch (err) {
				reject(err);
			}
		});
	}

	static GetIosDefaultLanguage(timeout = 8_000): Promise<string | null> {
		let timer: NodeJS.Timeout;

		return new Promise((resolve, reject) => {
			timer = setTimeout(() => {
				reject(null);
			}, timeout);

			try {
				(window as any).nativeEvent.exalusControllerData.handleLanguage = (lang: string) => {
					clearTimeout(timer);
					resolve(lang);
				};

				(window as any).webkit.messageHandlers.exalusControllerData.postMessage("GetDefaultLanguage");
			} catch (err) {
				reject(err);
			}
		});
	}
}


export interface IExalusDataMigration {
	IsMigrationDataAvailableAsync(): Promise<IsMigrationDataAvailableResponse | null>;
	GetLanguageCodeAsync(): Promise<string>;
	GetControllersDataAsync(): Promise<ControllerData[]>;
}

export interface ControllerData {
	AutoLogin: boolean;
	AutoLoginUser: AutoLoginUser;
	Name: string;
	PIN: string;
	SerialId: string;
}

export interface AutoLoginUser {
	Email: string;
	Password: string;
}

export interface IsMigrationDataAvailableResponse {
	Controllers: boolean;
	Language: boolean;
	DataMigrated: boolean;
}